import { useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { MetricCard } from "@/components/ui/MetricCard";
import { Toolbar, SegmentedFilter } from "@/components/ui/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { useAction, useItem, useList } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, duration, relative, text } from "@/lib/format";
import { OnecAuthPanel } from "./OnecAuth";
import { OneCStatusPanel } from "./OneCStatus";

/**
 * 1C integratsiyasi (dev_spec §11.1 «Integratsiya»).
 *
 * Kiruvchi va chiquvchi xabarlar, qo'lda ko'riladiganlar, xato sababi va
 * qayta ishlash; «1C holati» (avtomatik mapping natijasi, faqat o'qish —
 * qo'lda mapping 2026-10-31 da olib tashlandi). Payload ro'yxatda yo'q va
 * xabar kartasida ham YASHIRIN — faqat so'ralganda ochiladi
 * (dev_spec §11.2). Uni ko'radigan vakolat faqat administrator va 1C
 * texnik rolida.
 */

interface Message {
  direction: "INBOUND" | "OUTBOUND";
  message_id: string;
  event_type: string;
  entity_id?: string;
  version?: number;
  status: string;
  status_code?: string;
  status_message?: string;
  status_details?: Record<string, unknown>;
  attempts?: number;
  occurred_at?: string;
  received_at?: string;
  updated_at?: string;
  next_attempt_at?: string;
  mapping_version?: number;
  last_http_status?: number;
  payload?: unknown;
  onec_payload?: unknown;
  allowed_actions?: string[];
}

interface Health {
  enabled: boolean;
  callback_configured: boolean;
  inbound?: Record<string, number>;
  outbound?: Record<string, number>;
  oldest_pending_at?: string;
  oldest_pending_seconds?: number;
  mapping_version?: number;
}

type Tab = "INBOUND" | "OUTBOUND" | "review" | "status";

const STATUSES: Record<"INBOUND" | "OUTBOUND", string[]> = {
  INBOUND: ["ACCEPTED", "WAITING_MAPPING", "DISPATCHED", "APPLIED", "REJECTED", "SUPERSEDED", "FAILED"],
  OUTBOUND: ["PENDING", "WAITING_MAPPING", "DELIVERED", "MANUAL_REVIEW", "DEAD_LETTER"],
};

/** Yuborilmagan natija shundan eski bo'lsa 1C uzilgan deb hisoblanadi. */
const STALE_SECONDS = 15 * 60;

export function Integration() {
  const session = useSession();
  const [tab, setTab] = useState<Tab>("review");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [opened, setOpened] = useState<string | null>(null);
  const [showPayload, setShowPayload] = useState(false);
  const [retrying, setRetrying] = useState<Message | null>(null);
  const health = useItem<Health>("integration", "/api/v1/integration/health");
  const direction = tab === "INBOUND" || tab === "OUTBOUND" ? tab : undefined;
  const messages = useList<Message>(
    "integration",
    "/api/v1/integration/messages",
    { direction, status: status || undefined, entity_id: search.trim() || undefined, limit: 200 },
    { enabled: direction !== undefined },
  );
  const review = useList<Message>("integration", "/api/v1/integration/dead-letter", { limit: 200 }, {
    enabled: tab === "review",
  });
  const detail = useItem<Message>("integration", `/api/v1/integration/messages/${opened ?? ""}`, {
    enabled: opened !== null,
  });

  const retry = useAction<Message, Message>("integration", (message) => ({
    path: `/api/v1/integration/messages/${message.message_id}/retry`,
    method: "POST",
  }));
  const canRetry = session.can(PERM.integrationRetry);
  // 1C hisobi (Basic) — ulanish sozlamasi, mapping EMAS: o'sha vakolat.
  const canEditAuth = session.can(PERM.integrationMapping);
  const sum = (counts?: Record<string, number>, keys?: string[]) =>
    Object.entries(counts ?? {})
      .filter(([key]) => !keys || keys.includes(key))
      .reduce((total, [, value]) => total + value, 0);
  const stuck =
    sum(health.data?.inbound, ["WAITING_MAPPING", "REJECTED", "FAILED"]) +
    sum(health.data?.outbound, ["WAITING_MAPPING", "MANUAL_REVIEW", "DEAD_LETTER"]);
  const lag = health.data?.oldest_pending_seconds ?? 0;

  const messageColumns: Array<Column<Message>> = [
    ...(tab === "review"
      ? [
          {
            key: "direction",
            header: "Yo'nalish",
            render: (row: Message) => (
              <span style={{ whiteSpace: "nowrap" }}>{row.direction === "INBOUND" ? "1C → WMS" : "WMS → 1C"}</span>
            ),
          },
        ]
      : []),
    { key: "event", header: "Hodisa", render: (row) => <span className="mono">{row.event_type}</span> },
    {
      key: "entity",
      header: "Obyekt",
      render: (row) => (
        <span className="mono" title={row.entity_id}>
          {text(row.entity_id).slice(0, 18)}
          {row.version ? ` · v${row.version}` : ""}
        </span>
      ),
    },
    { key: "status", header: "Holat", render: (row) => <StatusBadge value={row.status} /> },
    {
      key: "reason",
      header: "Sabab",
      render: (row) =>
        row.status_code || row.status_message ? (
          <span title={row.status_message}>
            <span className="mono">{row.status_code ?? ""}</span> {row.status_message ?? ""}
          </span>
        ) : (
          "—"
        ),
    },
    { key: "attempts", header: "Urinish", align: "right", render: (row) => row.attempts ?? 0 },
    {
      key: "updated",
      header: "Yangilandi",
      render: (row) => {
        const at = row.updated_at ?? row.received_at;
        return <span title={dateTime(at)}>{relative(at)}</span>;
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <span style={{ display: "inline-flex", gap: 6 }}>
          <button
            type="button"
            className="btn btn-text btn-sm"
            onClick={() => {
              setShowPayload(false);
              setOpened(row.message_id);
            }}
          >
            <Icon name="search" size={14} />
            Ochish
          </button>
          {canRetry && row.allowed_actions?.includes("retry") && (
            <button type="button" className="btn btn-tonal btn-sm" onClick={() => setRetrying(row)}>
              <Icon name="refresh" size={14} />
              Qayta
            </button>
          )}
        </span>
      ),
    },
  ];

  const needle = search.trim().toLowerCase();
  const reviewRows = (review.data?.items ?? []).filter(
    (row) => !needle || [row.event_type, row.entity_id, row.status_code].some((value) => value?.toLowerCase().includes(needle)),
  );
  const message = detail.data;
  const missingMappings = Array.isArray(message?.status_details?.missing_mappings)
    ? (message?.status_details?.missing_mappings as Array<{ kind: string; external_key: string }>)
    : [];
  return (
    <>
      <PageHeader
        title="Integratsiya"
        hint="1C bilan almashinuv: kiruvchi va chiquvchi xabarlar, rad etilganlari va sababi, qayta ishlash. 1C qiymatlari avtomatik bog'lanadi."
      />

      <section className="metrics">
        <MetricCard
          label="1C almashinuvi"
          value={health.data ? (health.data.enabled ? "Yoqilgan" : "O'chiq") : undefined}
          hint={health.data?.callback_configured ? "Natija manzili sozlangan" : "Natija manzili sozlanmagan"}
          accent={health.data && (!health.data.enabled || !health.data.callback_configured) ? "warning" : "success"}
          loading={health.isLoading}
          icon="integration"
        />
        <MetricCard
          label="Ko'rib chiqish kerak"
          value={stuck}
          hint="1C qiymati kutayotgan, rad etilgan, yetkazilmagan"
          accent={stuck ? "warning" : "success"}
          loading={health.isLoading}
          icon="alert"
        />
        <MetricCard
          label="Yuborilmagan eng eskisi"
          value={lag >= 86400 ? `${Math.floor(lag / 86400)} kun` : duration(lag)}
          hint={lag > STALE_SECONDS ? "1C natijani qabul qilmayapti" : "Navbat yurmoqda"}
          accent={lag > STALE_SECONDS ? "error" : "success"}
          loading={health.isLoading}
          icon="refresh"
        />
        <MetricCard
          label="Avto-mapping versiyasi"
          value={health.data?.mapping_version ?? "—"}
          hint="Har avtomatik bog'lash versiyani oshiradi"
          loading={health.isLoading}
          icon="layers"
        />
      </section>

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Obyekt ID (1C GUID)…"
        onRefresh={() => {
          void health.refetch();
          if (tab !== "status") void (tab === "review" ? review : messages).refetch();
        }}
        refreshing={health.isFetching || messages.isFetching || review.isFetching}
      >
        <SegmentedFilter
          value={tab}
          onChange={(value) => {
            setTab(value);
            setStatus("");
          }}
          options={[
            { value: "review", label: "Ko'rib chiqish" },
            { value: "INBOUND", label: "1C → WMS" },
            { value: "OUTBOUND", label: "WMS → 1C" },
            { value: "status", label: "1C holati" },
          ]}
        />
        {direction && (
          <select className="input" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Holat">
            <option value="">Barcha holatlar</option>
            {STATUSES[direction].map((value) => (
              <option key={value} value={value}>
                {value}
                {health.data?.[direction === "INBOUND" ? "inbound" : "outbound"]?.[value] !== undefined
                  ? ` · ${health.data?.[direction === "INBOUND" ? "inbound" : "outbound"]?.[value]}`
                  : ""}
              </option>
            ))}
          </select>
        )}
      </Toolbar>

      {tab === "status" ? (
        <>
          <OnecAuthPanel canEdit={canEditAuth} />
          <OneCStatusPanel />
        </>
      ) : tab === "review" ? (
        <DataTable
          columns={messageColumns}
          rows={reviewRows}
          rowKey={(row) => `${row.direction}:${row.message_id}`}
          isLoading={review.isLoading}
          error={review.error}
          onRetry={() => void review.refetch()}
          emptyTitle="Qo'lda ko'riladigan xabar yo'q"
          emptyHint="Rad etilgan yoki 1C ga yetkazilmagan xabarlar shu yerga tushadi."
          footer={<span>{reviewRows.length} ta xabar</span>}
        />
      ) : (
        <DataTable
          columns={messageColumns}
          rows={messages.data?.items}
          rowKey={(row) => row.message_id}
          isLoading={messages.isLoading}
          error={messages.error}
          onRetry={() => void messages.refetch()}
          emptyTitle="Xabar topilmadi"
          hasMore={messages.data?.has_more}
          footer={<span>{messages.data?.items.length ?? 0} ta xabar</span>}
        />
      )}

      <ConfirmDialog
        open={opened !== null}
        title={message ? message.event_type : "Xabar"}
        description={
          message ? (
            <>
              {message.direction === "INBOUND" ? "1C → WMS" : "WMS → 1C"} · <span className="mono">{message.message_id}</span>
            </>
          ) : null
        }
        confirmLabel="Yopish"
        wide
        error={detail.error}
        extra={
          !message ? (
            <div className="skeleton" style={{ height: 160 }} />
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "6px 14px", margin: 0 }}>
                <dt>Holat</dt>
                <dd style={{ margin: 0 }}>
                  <StatusBadge value={message.status} />
                </dd>
                <dt>Obyekt</dt>
                <dd className="mono" style={{ margin: 0, overflowWrap: "anywhere" }}>
                  {text(message.entity_id)} {message.version ? `v${message.version}` : ""}
                </dd>
                {message.status_message && (
                  <>
                    <dt>Sabab</dt>
                    <dd style={{ margin: 0 }}>
                      <span className="mono">{message.status_code}</span> {message.status_message}
                    </dd>
                  </>
                )}
                {missingMappings.length > 0 && (
                  <>
                    <dt>1C qiymati aniqlanmadi</dt>
                    <dd style={{ margin: 0, display: "grid", gap: 6 }}>
                      {missingMappings.map((item) => (
                        <span key={`${item.kind}:${item.external_key}`} style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                          <StatusBadge value={item.kind} tone="warning" />
                          <span className="mono">{item.external_key}</span>
                        </span>
                      ))}
                    </dd>
                  </>
                )}
                {message.status_details && missingMappings.length === 0 && (
                  <>
                    <dt>Tafsilot</dt>
                    <dd className="mono" style={{ margin: 0, overflowWrap: "anywhere" }}>
                      {JSON.stringify(message.status_details)}
                    </dd>
                  </>
                )}
                <dt>Urinish</dt>
                <dd style={{ margin: 0 }}>
                  {message.attempts ?? 0}
                  {message.last_http_status ? ` · oxirgi HTTP ${message.last_http_status}` : ""}
                  {message.next_attempt_at ? ` · keyingisi ${dateTime(message.next_attempt_at)}` : ""}
                </dd>
                <dt>Vaqt</dt>
                <dd style={{ margin: 0 }}>
                  {dateTime(message.occurred_at ?? message.received_at)}
                  {message.mapping_version ? ` · mapping v${message.mapping_version}` : ""}
                </dd>
              </dl>
              {showPayload ? (
                <pre
                  className="mono"
                  style={{ maxHeight: 280, overflow: "auto", margin: 0, padding: 12, fontSize: 12, background: "var(--surface-container)", borderRadius: 8 }}
                >
                  {JSON.stringify(message.onec_payload ?? message.payload ?? null, null, 2)}
                </pre>
              ) : (
                <button type="button" className="btn btn-text btn-sm" style={{ justifySelf: "start" }} onClick={() => setShowPayload(true)}>
                  <Icon name="shield" size={14} />
                  Payloadni ko'rsatish
                </button>
              )}
            </div>
          )
        }
        onClose={() => setOpened(null)}
        onConfirm={() => setOpened(null)}
      />

      <ConfirmDialog
        open={retrying !== null}
        title="Xabarni qayta ishlash"
        description={
          retrying ? (
            <>
              <b>{retrying.event_type}</b> ayni message_id bilan navbatga qaytadi
              {retrying.direction === "OUTBOUND" ? ", urinishlar hisoblagichi nolga tushadi" : ""}. Takroriy hujjat
              yaratilmaydi.
            </>
          ) : null
        }
        confirmLabel="Qayta ishlash"
        busy={retry.isPending}
        error={retry.error}
        onClose={() => {
          setRetrying(null);
          retry.reset();
        }}
        onConfirm={() => {
          if (!retrying) return;
          retry.mutate(retrying, {
            onSuccess: () => {
              setRetrying(null);
              retry.reset();
            },
          });
        }}
      />

    </>
  );
}
